import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adjustStock, bulkCreateProducts, deleteProduct, deleteVariant, fetchStock, updateProduct, updateProductName, updateProductSortOrder, updateVariant } from '../services/db';
import { StockView } from '../types';
import { IconList, IconLayoutGrid, IconChevronDown, IconMenu } from '../components/Icons';
import { useI18n } from '../i18n';
import { AlertStatus, useAlertSettings } from '../contexts/AlertSettingsContext';

type ViewMode = 'LIST' | 'GROUPED';
type VariantSortMode = 'manual' | 'sku-asc' | 'sku-desc' | 'color-size-asc' | 'color-size-desc';

interface GroupedProduct {
  productId: number;
  productName: string;
  baseSku: string;
  totalStock: number;
  variants: StockView[];
}

export const Stock: React.FC = () => {
  const [stock, setStock] = useState<StockView[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('GROUPED');
  const [expandedProductIds, setExpandedProductIds] = useState<Set<number>>(new Set());
  const [editingProductId, setEditingProductId] = useState<number | null>(null);
  const [editingName, setEditingName] = useState('');
  const [savingProductId, setSavingProductId] = useState<number | null>(null);
  const [deletingProductId, setDeletingProductId] = useState<number | null>(null);
  const [editingProductDetailsId, setEditingProductDetailsId] = useState<number | null>(null);
  const [productDetails, setProductDetails] = useState({
    name: '',
    baseSku: '',
    description: '',
    displayColor: '#111827',
    iconUrl: '',
    active: true
  });
  const [productDetailsError, setProductDetailsError] = useState('');
  const [savingProductDetailsId, setSavingProductDetailsId] = useState<number | null>(null);
  const [renameError, setRenameError] = useState('');
  const [editingStockId, setEditingStockId] = useState<number | null>(null);
  const [editingStockValue, setEditingStockValue] = useState<number | ''>('');
  const [savingStockId, setSavingStockId] = useState<number | null>(null);
  const [editingMatrixCellId, setEditingMatrixCellId] = useState<number | null>(null);
  const [editingMatrixValue, setEditingMatrixValue] = useState<number | ''>('');
  const [savingMatrixCellId, setSavingMatrixCellId] = useState<number | null>(null);
  const [deletingVariantId, setDeletingVariantId] = useState<number | null>(null);
  const [editingVariantId, setEditingVariantId] = useState<number | null>(null);
  const [variantDetails, setVariantDetails] = useState({
    sku: '',
    color: '',
    size: '',
    active: true
  });
  const [variantDetailsError, setVariantDetailsError] = useState('');
  const [savingVariantId, setSavingVariantId] = useState<number | null>(null);
  const [reorderingProductId, setReorderingProductId] = useState<number | null>(null);
  const [reorderingVariantId, setReorderingVariantId] = useState<number | null>(null);
  const [variantSortModes, setVariantSortModes] = useState<Record<number, VariantSortMode>>({});
  const [openActionMenuId, setOpenActionMenuId] = useState<number | null>(null);
  const [toast, setToast] = useState<{ msg: string, type: 'success' | 'error' } | null>(null);
  const { thresholds, setGlobalMinimum, setCategoryMinimum, removeCategoryMinimum, getThresholdForCategory, getStatusForQuantity } = useAlertSettings();
  const [categoryKey, setCategoryKey] = useState('');
  const [categoryThreshold, setCategoryThreshold] = useState(thresholds.globalMinimum);
  const [bulkName, setBulkName] = useState('');
  const [bulkModel, setBulkModel] = useState('');
  const [bulkColors, setBulkColors] = useState('');
  const [bulkSizes, setBulkSizes] = useState('');
  const [bulkSeparator, setBulkSeparator] = useState('-');
  const [bulkError, setBulkError] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const { t } = useI18n();
  const [areFiltersOpen, setAreFiltersOpen] = useState(true);

  const statusStyles: Record<AlertStatus, { text: string; bg: string; border: string }> = {
    ok: { text: 'text-success', bg: 'bg-success-soft', border: 'border-success' },
    attention: { text: 'text-warning', bg: 'bg-warning-soft', border: 'border-warning' },
    critical: { text: 'text-danger', bg: 'bg-danger-soft', border: 'border-danger' },
  };

  const globalBuffer = useMemo(
    () => Math.max(1, Math.round(thresholds.globalMinimum * 0.5)),
    [thresholds.globalMinimum]
  );

  const formatProductName = useCallback((name?: string | null) => name?.trim() || t('stock.unknownProduct'), [t]);

  const loadStock = async () => {
    setLoading(true);
    const data = await fetchStock(search);
    setStock(data);
    setLoading(false);
  };

  useEffect(() => {
    const timer = setTimeout(loadStock, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const stored = window.localStorage.getItem('stockFiltersOpen');
    if (stored !== null) {
      setAreFiltersOpen(stored === 'true');
      return;
    }

    setAreFiltersOpen(window.innerWidth >= 768);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem('stockFiltersOpen', String(areFiltersOpen));
  }, [areFiltersOpen]);

  useEffect(() => {
    setCategoryThreshold(thresholds.globalMinimum);
  }, [thresholds.globalMinimum]);

  useEffect(() => {
    if (!toast) return;

    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  const availableCategories = useMemo(
    () => Array.from(new Set(stock.map(item => item.base_sku).filter(Boolean) as string[])),
    [stock]
  );

  // Grouping Logic
  const groupedStock: GroupedProduct[] = useMemo(() => {
    if (viewMode === 'LIST') return [];

    const groups: Record<number, GroupedProduct> = {};

    stock.forEach(item => {
      if (!groups[item.product_id]) {
        groups[item.product_id] = {
          productId: item.product_id,
          productName: formatProductName(item.productName),
          baseSku: item.base_sku || item.sku || 'UNK',
          totalStock: 0,
          variants: []
        };
      }
      groups[item.product_id].variants.push(item);
      groups[item.product_id].totalStock += item.current_stock;
    });

    return Object.values(groups);
  }, [stock, viewMode, t, formatProductName]);

  const sortedGroupedStock = useMemo(
    () => groupedStock.slice().sort((a, b) => {
      const sortA = a.variants[0]?.productSortOrder ?? 0;
      const sortB = b.variants[0]?.productSortOrder ?? 0;
      if (sortA !== sortB) return sortA - sortB;
      return a.productName.localeCompare(b.productName);
    }),
    [groupedStock]
  );

  const toggleExpand = (id: number) => {
    setExpandedProductIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleActionMenu = (id: number) => {
    setOpenActionMenuId(prev => (prev === id ? null : id));
  };

  const startEditing = (productId: number, currentName: string) => {
    setEditingProductId(productId);
    setEditingName(formatProductName(currentName));
    setRenameError('');
  };

  const cancelEditing = () => {
    setEditingProductId(null);
    setEditingName('');
    setRenameError('');
  };

  const handleRename = async (productId: number) => {
    if (!editingName.trim()) return;
    const trimmedName = editingName.trim();
    const previousName = stock.find(item => item.product_id === productId)?.productName || '';

    setSavingProductId(productId);
    setStock(prev => prev.map(item => item.product_id === productId ? { ...item, productName: trimmedName } : item));
    setRenameError('');

    try {
      await updateProductName(productId, trimmedName);
    } catch (error) {
      setStock(prev => prev.map(item => item.product_id === productId ? { ...item, productName: previousName } : item));
      setRenameError(t('stock.renameError'));
      return;
    } finally {
      setSavingProductId(null);
    }

    setEditingProductId(null);
    setEditingName('');
  };

  const startEditingProductDetails = (productId: number) => {
    const product = stock.find(item => item.product_id === productId);
    if (!product) return;
    setEditingProductDetailsId(productId);
    setProductDetails({
      name: product.productName || '',
      baseSku: product.base_sku || '',
      description: product.productDescription || '',
      displayColor: product.productDisplayColor || '#111827',
      iconUrl: product.productIconUrl || '',
      active: product.productActive !== undefined ? product.productActive : true
    });
    setProductDetailsError('');
  };

  const cancelEditingProductDetails = () => {
    setEditingProductDetailsId(null);
    setProductDetails({
      name: '',
      baseSku: '',
      description: '',
      displayColor: '#111827',
      iconUrl: '',
      active: true
    });
    setProductDetailsError('');
  };

  const handleSaveProductDetails = async (productId: number) => {
    if (!productDetails.name.trim() || !productDetails.baseSku.trim()) {
      setProductDetailsError(t('stock.validation.requiredFields'));
      return;
    }

    setSavingProductDetailsId(productId);
    setProductDetailsError('');

    try {
      await updateProduct(productId, {
        name: productDetails.name.trim(),
        base_sku: productDetails.baseSku.trim(),
        description: productDetails.description.trim(),
        display_color: productDetails.displayColor.trim(),
        icon_url: productDetails.iconUrl.trim(),
        active: productDetails.active
      });

      if (!productDetails.active) {
        setStock(prev => prev.filter(item => item.product_id !== productId));
      } else {
        setStock(prev => prev.map(item => (
          item.product_id === productId
            ? {
                ...item,
                productName: productDetails.name.trim(),
                base_sku: productDetails.baseSku.trim(),
                productDescription: productDetails.description.trim(),
                productDisplayColor: productDetails.displayColor.trim(),
                productIconUrl: productDetails.iconUrl.trim(),
                productActive: productDetails.active
              }
            : item
        )));
      }

      setToast({ msg: t('stock.updateProductSuccess'), type: 'success' });
      cancelEditingProductDetails();
    } catch (error) {
      const message = (error as Error).message || '';
      if (message.includes('Base SKU already exists')) {
        setProductDetailsError(t('stock.validation.baseSkuExists'));
      } else {
        setProductDetailsError(t('stock.updateProductError'));
      }
    } finally {
      setSavingProductDetailsId(null);
    }
  };

  const handleMoveProduct = async (productId: number, direction: 'up' | 'down') => {
    const currentIndex = sortedGroupedStock.findIndex(group => group.productId === productId);
    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= sortedGroupedStock.length) {
      return;
    }

    const currentGroup = sortedGroupedStock[currentIndex];
    const targetGroup = sortedGroupedStock[targetIndex];
    const currentSort = currentGroup.variants[0]?.productSortOrder ?? 0;
    const targetSort = targetGroup.variants[0]?.productSortOrder ?? 0;

    setReorderingProductId(productId);
    try {
      await updateProductSortOrder(currentGroup.productId, targetSort);
      await updateProductSortOrder(targetGroup.productId, currentSort);

      setStock(prev => prev.map(item => {
        if (item.product_id === currentGroup.productId) {
          return { ...item, productSortOrder: targetSort };
        }
        if (item.product_id === targetGroup.productId) {
          return { ...item, productSortOrder: currentSort };
        }
        return item;
      }));
    } catch (error) {
      setToast({ msg: t('stock.reorderError'), type: 'error' });
    } finally {
      setReorderingProductId(null);
    }
  };

  const handleDeactivateProduct = async (productId: number, productName: string) => {
    const confirmed = window.confirm(t('stock.confirmDeleteProduct', { name: productName }));
    if (!confirmed) return;

    setDeletingProductId(productId);

    try {
      await deleteProduct(productId);
      setStock(prev => prev.filter(item => item.product_id !== productId));
      setToast({ msg: t('stock.deleteSuccessProduct'), type: 'success' });
    } catch (error) {
      setToast({ msg: t('stock.deleteErrorProduct'), type: 'error' });
    } finally {
      setDeletingProductId(null);
    }
  };

  const handleAddCategory = () => {
    if (!categoryKey.trim()) return;
    setCategoryMinimum(categoryKey.trim(), categoryThreshold);
    setCategoryKey('');
  };

  const parseBulkList = (value: string) => value
    .split(/[\n,]+/)
    .map(item => item.trim())
    .filter(Boolean);

  const handleBulkCreate = async () => {
    const colors = parseBulkList(bulkColors);
    const sizes = parseBulkList(bulkSizes);

    if (!bulkName.trim() || !bulkModel.trim() || colors.length === 0 || sizes.length === 0) {
      setBulkError('Please fill name, model, colors, and sizes.');
      return;
    }

    setBulkSaving(true);
    setBulkError('');

    try {
      await bulkCreateProducts({
        name: bulkName.trim(),
        model: bulkModel.trim(),
        colors,
        sizes,
        separator: bulkSeparator.trim() || '-'
      });
      setBulkName('');
      setBulkModel('');
      setBulkColors('');
      setBulkSizes('');
      setBulkSeparator('-');
      setToast({ msg: 'Bulk creation successful.', type: 'success' });
      await loadStock();
    } catch (error) {
      setBulkError((error as Error).message || 'Failed to bulk create.');
      setToast({ msg: 'Bulk creation failed.', type: 'error' });
    } finally {
      setBulkSaving(false);
    }
  };

  const startEditingStock = (variantId: number, currentStock: number) => {
    setEditingStockId(variantId);
    setEditingStockValue(currentStock);
  };

  const cancelEditingStock = () => {
    setEditingStockId(null);
    setEditingStockValue('');
  };

  const handleConfirmStock = async (variant: StockView) => {
    if (editingStockValue === '') return;

    const newQuantity = Number(editingStockValue);
    setSavingStockId(variant.id);

    try {
      const result = await adjustStock(variant.id, newQuantity);

      setStock(prev => prev.map(item => item.id === variant.id ? { ...item, current_stock: result.newStock } : item));
      setToast({ msg: t('stock.adjust.success', { value: result.newStock }), type: 'success' });
    } catch (err) {
      setToast({ msg: t('stock.adjust.error'), type: 'error' });
    } finally {
      setSavingStockId(null);
      cancelEditingStock();
    }
  };

  const handleDeactivateVariant = async (variant: StockView) => {
    const label = variant.sku || `${variant.color}-${variant.size}`;
    const confirmed = window.confirm(t('stock.confirmDeleteVariant', { name: label }));
    if (!confirmed) return;

    setDeletingVariantId(variant.id);

    try {
      await deleteVariant(variant.id);
      setStock(prev => prev.filter(item => item.id !== variant.id));
      setToast({ msg: t('stock.deleteSuccessVariant'), type: 'success' });
    } catch (error) {
      setToast({ msg: t('stock.deleteErrorVariant'), type: 'error' });
    } finally {
      setDeletingVariantId(null);
    }
  };

  const startEditingMatrixCell = (variant: StockView) => {
    setEditingMatrixCellId(variant.id);
    setEditingMatrixValue(variant.current_stock);
  };

  const cancelEditingMatrixCell = () => {
    setEditingMatrixCellId(null);
    setEditingMatrixValue('');
  };

  const handleConfirmMatrixCell = async (variant: StockView) => {
    if (editingMatrixValue === '') return;

    const newQuantity = Number(editingMatrixValue);
    setSavingMatrixCellId(variant.id);

    try {
      const result = await adjustStock(variant.id, newQuantity);
      setStock(prev => prev.map(item => item.id === variant.id ? { ...item, current_stock: result.newStock } : item));
      setToast({ msg: t('stock.adjust.success', { value: result.newStock }), type: 'success' });
    } catch (err) {
      setToast({ msg: t('stock.adjust.error'), type: 'error' });
    } finally {
      setSavingMatrixCellId(null);
      cancelEditingMatrixCell();
    }
  };

  const startEditingVariantDetails = (variant: StockView) => {
    setEditingVariantId(variant.id);
    setVariantDetails({
      sku: variant.sku || '',
      color: variant.color || '',
      size: variant.size || '',
      active: variant.active
    });
    setVariantDetailsError('');
  };

  const cancelEditingVariantDetails = () => {
    setEditingVariantId(null);
    setVariantDetails({
      sku: '',
      color: '',
      size: '',
      active: true
    });
    setVariantDetailsError('');
  };

  const handleSaveVariantDetails = async (variant: StockView) => {
    if (!variantDetails.sku.trim()) {
      setVariantDetailsError(t('stock.validation.skuRequired'));
      return;
    }

    setSavingVariantId(variant.id);
    setVariantDetailsError('');

    try {
      await updateVariant(variant.id, {
        sku: variantDetails.sku.trim(),
        color: variantDetails.color.trim(),
        size: variantDetails.size.trim(),
        active: variantDetails.active
      });

      if (!variantDetails.active) {
        setStock(prev => prev.filter(item => item.id !== variant.id));
      } else {
        setStock(prev => prev.map(item => (
          item.id === variant.id
            ? {
                ...item,
                sku: variantDetails.sku.trim(),
                color: variantDetails.color.trim(),
                size: variantDetails.size.trim(),
                active: variantDetails.active
              }
            : item
        )));
      }

      setToast({ msg: t('stock.updateVariantSuccess'), type: 'success' });
      cancelEditingVariantDetails();
    } catch (error) {
      const message = (error as Error).message || '';
      if (message.includes('SKU already exists')) {
        setVariantDetailsError(t('stock.validation.skuExists'));
      } else {
        setVariantDetailsError(t('stock.updateVariantError'));
      }
    } finally {
      setSavingVariantId(null);
    }
  };

  const getVariantSortMode = (productId: number): VariantSortMode => variantSortModes[productId] ?? 'manual';

  const sortVariants = useCallback((variants: StockView[], mode: VariantSortMode) => {
    const sorted = variants.slice();
    const compareText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

    switch (mode) {
      case 'manual':
        return sorted.sort((a, b) => {
          const orderA = a.sort_order ?? 0;
          const orderB = b.sort_order ?? 0;
          if (orderA !== orderB) return orderA - orderB;
          return compareText(a.sku, b.sku);
        });
      case 'sku-asc':
        return sorted.sort((a, b) => compareText(a.sku, b.sku));
      case 'sku-desc':
        return sorted.sort((a, b) => compareText(b.sku, a.sku));
      case 'color-size-asc':
        return sorted.sort((a, b) => {
          const colorCompare = compareText(a.color, b.color);
          if (colorCompare !== 0) return colorCompare;
          const sizeCompare = compareText(a.size, b.size);
          if (sizeCompare !== 0) return sizeCompare;
          return compareText(a.sku, b.sku);
        });
      case 'color-size-desc':
        return sorted.sort((a, b) => {
          const colorCompare = compareText(b.color, a.color);
          if (colorCompare !== 0) return colorCompare;
          const sizeCompare = compareText(b.size, a.size);
          if (sizeCompare !== 0) return sizeCompare;
          return compareText(b.sku, a.sku);
        });
      default:
        return sorted;
    }
  }, []);

  const handleMoveVariant = async (productId: number, variantId: number, direction: 'up' | 'down') => {
    const currentMode = getVariantSortMode(productId);
    if (currentMode !== 'manual') return;

    const variants = stock.filter(item => item.product_id === productId);
    const sortedVariants = sortVariants(variants, 'manual');
    const currentIndex = sortedVariants.findIndex(variant => variant.id === variantId);
    if (currentIndex === -1) return;

    const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (targetIndex < 0 || targetIndex >= sortedVariants.length) return;

    const currentVariant = sortedVariants[currentIndex];
    const targetVariant = sortedVariants[targetIndex];
    const currentOrder = currentVariant.sort_order ?? 0;
    const targetOrder = targetVariant.sort_order ?? 0;

    setReorderingVariantId(variantId);
    setStock(prev => prev.map(item => {
      if (item.id === currentVariant.id) return { ...item, sort_order: targetOrder };
      if (item.id === targetVariant.id) return { ...item, sort_order: currentOrder };
      return item;
    }));

    try {
      await Promise.all([
        updateVariant(currentVariant.id, { sort_order: targetOrder }),
        updateVariant(targetVariant.id, { sort_order: currentOrder })
      ]);
    } catch (error) {
      setStock(prev => prev.map(item => {
        if (item.id === currentVariant.id) return { ...item, sort_order: currentOrder };
        if (item.id === targetVariant.id) return { ...item, sort_order: targetOrder };
        return item;
      }));
      setToast({ msg: t('stock.reorderVariantError'), type: 'error' });
    } finally {
      setReorderingVariantId(null);
    }
  };

  // Matrix (Pivot) Rendering Helper
  const renderProductMatrix = (variants: StockView[], sortMode: VariantSortMode, category?: string | null) => {
    const orderedVariants = sortVariants(variants, sortMode);
    const uniqueInOrder = (items: string[]) => {
      const seen = new Set<string>();
      return items.filter(item => {
        if (seen.has(item)) return false;
        seen.add(item);
        return true;
      });
    };
    const compareText = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' });

    const sizes = sortMode === 'color-size-asc'
      ? Array.from(new Set(variants.map(v => v.size))).sort(compareText)
      : sortMode === 'color-size-desc'
        ? Array.from(new Set(variants.map(v => v.size))).sort((a, b) => compareText(b, a))
        : uniqueInOrder(orderedVariants.map(v => v.size));
    const colors = sortMode === 'color-size-asc'
      ? Array.from(new Set(variants.map(v => v.color))).sort(compareText)
      : sortMode === 'color-size-desc'
        ? Array.from(new Set(variants.map(v => v.color))).sort((a, b) => compareText(b, a))
        : uniqueInOrder(orderedVariants.map(v => v.color));

    // 2. Build Lookup Map
    const stockMap: Record<string, number> = {};
    const variantMap: Record<string, StockView> = {};
    variants.forEach(v => {
      stockMap[`${v.color}_${v.size}`] = v.current_stock;
      variantMap[`${v.color}_${v.size}`] = v;
    });

    return (
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-center border-collapse">
          <thead>
            <tr>
              <th className="p-2 border border-subtle bg-surface-strong text-left text-muted font-medium">{t('stock.table.color')}</th>
              {sizes.map(s => (
                <th key={s} className="p-2 border border-subtle bg-surface-strong font-medium text-primary">{s}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {colors.map(color => (
              <tr key={color}>
                <td className="p-2 border border-subtle font-medium text-left text-primary bg-surface-strong">{color}</td>
                {sizes.map(size => {
                  const qty = stockMap[`${color}_${size}`];
                  const variant = variantMap[`${color}_${size}`];
                  const hasStock = qty !== undefined;
                  const status = getStatusForQuantity(qty, category);
                  const style = statusStyles[status];
                  const isEditingCell = variant && editingMatrixCellId === variant.id;
                  const isSavingCell = variant && savingMatrixCellId === variant.id;
                  return (
                    <td key={size} className="p-2 border border-subtle">
                      {hasStock ? (
                        isEditingCell && variant ? (
                          <div className="flex flex-col items-center gap-1">
                            <input
                              type="number"
                              value={editingMatrixValue}
                              onChange={(event) => setEditingMatrixValue(event.target.value === '' ? '' : Number(event.target.value))}
                              className={`w-20 px-2 py-1 text-sm font-semibold rounded-md border text-center ${style.bg} ${style.text} ${style.border}`}
                              disabled={isSavingCell}
                            />
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                className="px-2 py-1 text-[11px] font-semibold rounded-md border border-subtle text-primary hover:bg-surface-strong disabled:opacity-50"
                                onClick={() => handleConfirmMatrixCell(variant)}
                                disabled={isSavingCell || editingMatrixValue === ''}
                              >
                                {isSavingCell ? t('stock.saving') : t('stock.save')}
                              </button>
                              <button
                                type="button"
                                className="px-2 py-1 text-[11px] font-semibold rounded-md border border-subtle text-primary hover:bg-surface-strong disabled:opacity-50"
                                onClick={cancelEditingMatrixCell}
                                disabled={isSavingCell}
                              >
                                {t('stock.cancel')}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => variant && startEditingMatrixCell(variant)}
                            className={`inline-flex items-center justify-center min-w-[48px] px-2 py-1 text-sm font-semibold rounded-md border ${style.bg} ${style.text} ${style.border}`}
                          >
                            {qty}
                          </button>
                        )
                      ) : (
                        <span className="text-muted">-</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="space-y-4 text-primary">
      <div className="bg-surface p-4 rounded-xl shadow-sm border border-subtle sticky top-0 z-10 space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
           <div>
             <h2 className="text-lg font-bold text-primary">{t('stock.title')}</h2>
             <p className="text-sm text-muted">{t('stock.alertSettings.subtitle')}</p>
           </div>

           <div className="flex items-center gap-2 self-start md:self-auto">
             <button
               type="button"
               onClick={() => setAreFiltersOpen(prev => !prev)}
               aria-expanded={areFiltersOpen}
               aria-controls="stock-filters"
               className="inline-flex items-center gap-2 px-3 py-2 text-xs font-semibold rounded-md border border-subtle bg-surface-strong text-primary hover:bg-surface"
             >
               <span>{areFiltersOpen ? 'Hide filters' : 'Show filters'}</span>
               <IconChevronDown className={`w-4 h-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} />
             </button>

             {/* View Toggles */}
             <div className="flex bg-surface-strong p-1 rounded-lg border border-subtle">
               <button
                 onClick={() => setViewMode('GROUPED')}
                 className={`p-2 rounded-md transition-all ${viewMode === 'GROUPED' ? 'bg-surface shadow text-accent border border-accent' : 'text-muted hover:text-primary'}`}
               >
                 <IconLayoutGrid className="w-4 h-4" />
               </button>
               <button
                 onClick={() => setViewMode('LIST')}
                 className={`p-2 rounded-md transition-all ${viewMode === 'LIST' ? 'bg-surface shadow text-accent border border-accent' : 'text-muted hover:text-primary'}`}
               >
                 <IconList className="w-4 h-4" />
               </button>
             </div>
           </div>
        </div>

        <div
          id="stock-filters"
          className={`space-y-4 overflow-hidden transition-[max-height,opacity] duration-300 ${areFiltersOpen ? 'opacity-100' : 'opacity-0'}`}
          style={{ maxHeight: areFiltersOpen ? '2000px' : '0px' }}
          aria-hidden={!areFiltersOpen}
        >
          <input
            type="text"
            placeholder={t('stock.search.placeholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-4 py-2 bg-surface-strong border border-subtle rounded-lg text-sm focus:ring-2 focus:ring-[var(--color-focus)] outline-none"
          />

          <div className="grid md:grid-cols-2 gap-3">
            <div className="p-3 rounded-lg border border-subtle bg-surface-strong space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-primary">{t('stock.alertSettings.globalTitle')}</p>
                  <p className="text-xs text-muted">{t('stock.alertSettings.globalHint')}</p>
                </div>
                <span className="inline-flex items-center gap-2 px-2 py-1 text-[11px] rounded-md border border-subtle text-muted bg-surface">
                  {t('stock.alertSettings.currentThreshold', { value: thresholds.globalMinimum })}
                </span>
              </div>
              <div className="flex flex-col gap-2 md:flex-row md:items-center">
                <label className="text-xs font-semibold text-muted md:w-32" htmlFor="global-threshold">{t('stock.alertSettings.globalLabel')}</label>
                <input
                  id="global-threshold"
                  type="number"
                  min={0}
                  value={thresholds.globalMinimum}
                  onChange={(e) => setGlobalMinimum(Number(e.target.value))}
                  className="w-full md:flex-1 px-3 py-2 bg-surface border border-subtle rounded-lg text-sm"
                />
              </div>
              <div className="flex items-center gap-2 text-xs text-muted">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md border ${statusStyles.ok.bg} ${statusStyles.ok.text} ${statusStyles.ok.border}`}>
                  {t('stock.alertSettings.bufferLabel', { value: globalBuffer })}
                </span>
                <span>{t('stock.alertSettings.bufferHint')}</span>
              </div>
            </div>

            <div className="p-3 rounded-lg border border-subtle bg-surface-strong space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-semibold text-primary">{t('stock.alertSettings.categoryTitle')}</p>
                  <p className="text-xs text-muted">{t('stock.alertSettings.categoryHint')}</p>
                </div>
                {availableCategories.length > 0 && (
                  <span className="text-[11px] text-muted">{t('stock.alertSettings.detectedCategories', { count: availableCategories.length })}</span>
                )}
              </div>
              <div className="flex flex-col gap-2 md:flex-row">
                <input
                  type="text"
                  value={categoryKey}
                  placeholder={t('stock.alertSettings.categoryPlaceholder')}
                  onChange={(e) => setCategoryKey(e.target.value)}
                  className="flex-1 px-3 py-2 bg-surface border border-subtle rounded-lg text-sm"
                />
                <input
                  type="number"
                  min={0}
                  value={categoryThreshold}
                  onChange={(e) => setCategoryThreshold(Number(e.target.value))}
                  className="w-full md:w-32 px-3 py-2 bg-surface border border-subtle rounded-lg text-sm"
                />
                <button
                  onClick={handleAddCategory}
                  className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent disabled:opacity-50"
                  disabled={!categoryKey.trim()}
                >
                  {t('stock.alertSettings.addCategory')}
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {Object.entries(thresholds.categoryMinimums).length === 0 && (
                  <span className="text-xs text-muted">{t('stock.alertSettings.noCategories')}</span>
                )}
                {Object.entries(thresholds.categoryMinimums).map(([category, min]) => (
                  <span key={category} className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-subtle bg-surface text-xs font-semibold">
                    <span className="text-primary">{category}</span>
                    <span className="text-muted">{t('stock.alertSettings.thresholdValue', { value: min })}</span>
                    <button
                      onClick={() => removeCategoryMinimum(category)}
                      className="text-danger text-[11px] font-bold hover:underline"
                      aria-label={t('stock.alertSettings.removeCategory', { category })}
                    >
                      {t('stock.alertSettings.removeLabel')}
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="p-3 rounded-lg border border-subtle bg-surface-strong space-y-3">
            <div>
              <p className="text-sm font-semibold text-primary">Bulk create</p>
              <p className="text-xs text-muted">Create a product and all color/size variants in one step.</p>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted" htmlFor="bulk-name">Name</label>
                <input
                  id="bulk-name"
                  value={bulkName}
                  onChange={(event) => setBulkName(event.target.value)}
                  className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-sm"
                  placeholder="Product name"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted" htmlFor="bulk-model">Model</label>
                <input
                  id="bulk-model"
                  value={bulkModel}
                  onChange={(event) => setBulkModel(event.target.value)}
                  className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-sm"
                  placeholder="Model or line"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted" htmlFor="bulk-colors">Colors (comma or newline)</label>
                <textarea
                  id="bulk-colors"
                  value={bulkColors}
                  onChange={(event) => setBulkColors(event.target.value)}
                  className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-sm min-h-[80px]"
                  placeholder="Red, Blue, Green"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted" htmlFor="bulk-sizes">Sizes (comma or newline)</label>
                <textarea
                  id="bulk-sizes"
                  value={bulkSizes}
                  onChange={(event) => setBulkSizes(event.target.value)}
                  className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface text-sm min-h-[80px]"
                  placeholder="S, M, L"
                />
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs font-semibold text-muted" htmlFor="bulk-separator">Separator</label>
                <input
                  id="bulk-separator"
                  value={bulkSeparator}
                  onChange={(event) => setBulkSeparator(event.target.value)}
                  className="w-24 px-3 py-2 border border-subtle rounded-lg bg-surface text-sm"
                  placeholder="-"
                />
              </div>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent disabled:opacity-50"
                onClick={handleBulkCreate}
                disabled={bulkSaving}
              >
                {bulkSaving ? t('stock.saving') : 'Create variants'}
              </button>
            </div>
            {bulkError && <p className="text-xs text-danger">{bulkError}</p>}
          </div>

          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-[11px] uppercase text-muted font-semibold">{t('stock.alertSettings.legendTitle')}</span>
            {(Object.keys(statusStyles) as AlertStatus[]).map((status) => (
              <span key={status} className={`inline-flex items-center gap-2 px-3 py-2 rounded-md border font-semibold ${statusStyles[status].bg} ${statusStyles[status].text} ${statusStyles[status].border}`}>
                {t(`stock.alertSettings.legend.${status}` as const)}
                <span className="text-[11px] text-muted font-normal">
                  {status === 'critical'
                    ? t('stock.alertSettings.legendCritical', { value: thresholds.globalMinimum })
                    : status === 'attention'
                      ? t('stock.alertSettings.legendAttention', { value: thresholds.globalMinimum + globalBuffer })
                      : t('stock.alertSettings.legendOk', { value: thresholds.globalMinimum + globalBuffer })}
                </span>
              </span>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center p-8 text-muted">{t('stock.loading')}</div>
      ) : stock.length === 0 ? (
        <div className="text-center p-8 text-muted">{t('stock.empty')}</div>
      ) : (
        <div className="space-y-3">

          {/* GROUPED VIEW */}
          {viewMode === 'GROUPED' && sortedGroupedStock.map((group, index) => {
            const isExpanded = expandedProductIds.has(group.productId);
            const isEditing = editingProductId === group.productId;
            const category = group.baseSku || group.productName;
            const thresholdForCategory = getThresholdForCategory(category);
            const totalStatus = getStatusForQuantity(group.totalStock, category);
            const totalStyle = statusStyles[totalStatus];
            const isEditingDetails = editingProductDetailsId === group.productId;
            const isReordering = reorderingProductId === group.productId;
            const productDisplayColor = group.variants[0]?.productDisplayColor || '#111827';
            const productIconUrl = group.variants[0]?.productIconUrl || '';
            const variantSortMode = getVariantSortMode(group.productId);
            const sortedVariants = sortVariants(group.variants, variantSortMode);
            const isManualSort = variantSortMode === 'manual';
            return (
              <div key={group.productId} className="bg-surface rounded-xl shadow-sm border border-subtle overflow-hidden transition-all">
                <div className="p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <button
                      onClick={() => toggleExpand(group.productId)}
                      className="flex-1 flex items-stretch justify-between text-left hover:bg-surface-strong transition-colors rounded-lg px-2 py-1 h-20"
                    >
                      <div className="flex items-stretch gap-3 min-w-0">
                        <div className="flex items-center">
                          <span
                            className="inline-flex h-3 w-3 rounded-full border border-subtle"
                            style={{ backgroundColor: productDisplayColor }}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="h-full w-20 flex-shrink-0">
                          {productIconUrl ? (
                            <img
                              src={productIconUrl}
                              alt={t('stock.productIconAlt', { name: group.productName })}
                              className="h-full w-full rounded-md border border-subtle object-cover"
                            />
                          ) : (
                            <span className="flex h-full w-full items-center justify-center rounded-md border border-dashed border-subtle bg-surface-strong" />
                          )}
                        </div>
                        <div className="flex flex-col justify-center min-w-0">
                          <h3 className="font-bold text-primary text-base md:text-lg leading-tight line-clamp-2 break-words">{group.productName}</h3>
                          <div className="text-xs text-muted font-mono mt-1">{group.baseSku} REF</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                           <span className="block text-xs text-muted uppercase font-semibold">{t('stock.total')}</span>
                           <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-md border text-sm font-bold ${totalStyle.bg} ${totalStyle.text} ${totalStyle.border}`}>
                             {group.totalStock}
                             <span className="text-[11px] text-muted font-semibold">{t('stock.alertSettings.thresholdValue', { value: thresholdForCategory })}</span>
                           </span>
                        </div>
                        <IconChevronDown className={`w-5 h-5 text-muted transition-transform ${isExpanded ? 'rotate-180' : ''}`}/>
                      </div>
                    </button>
                    <div
                      className="relative flex flex-col items-end gap-1 min-w-[200px]"
                      onBlur={(event) => {
                        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                          setOpenActionMenuId(null);
                        }
                      }}
                    >
                      <span className="text-[10px] uppercase text-muted font-semibold tracking-wide">{t('stock.editName')}</span>
                      <button
                        type="button"
                        className="inline-flex items-center justify-center px-2 py-2 rounded-md border border-subtle text-primary hover:bg-surface-strong"
                        onClick={() => toggleActionMenu(group.productId)}
                        aria-expanded={openActionMenuId === group.productId}
                        aria-controls={`group-actions-${group.productId}`}
                        aria-haspopup="menu"
                      >
                        <IconMenu className="h-4 w-4" />
                        <span className="sr-only">{t('stock.editDetails')}</span>
                      </button>
                      {openActionMenuId === group.productId && (
                        <div
                          id={`group-actions-${group.productId}`}
                          role="menu"
                          className="absolute right-0 top-10 z-20 w-48 rounded-lg border border-subtle bg-surface shadow-lg p-1"
                        >
                          {!isEditing && (
                            <button
                              role="menuitem"
                              className="w-full text-left px-3 py-2 text-xs font-semibold rounded-md text-primary hover:bg-surface-strong"
                              onClick={() => {
                                startEditing(group.productId, group.productName);
                                setOpenActionMenuId(null);
                              }}
                            >
                              {t('stock.editName')}
                            </button>
                          )}
                          {isEditing && (
                            <span className="block px-3 py-2 text-xs text-muted">{t('stock.renameLabel')}</span>
                          )}
                          {!isEditingDetails && (
                            <button
                              role="menuitem"
                              className="w-full text-left px-3 py-2 text-xs font-semibold rounded-md text-primary hover:bg-surface-strong"
                              onClick={() => {
                                startEditingProductDetails(group.productId);
                                setOpenActionMenuId(null);
                              }}
                            >
                              {t('stock.editDetails')}
                            </button>
                          )}
                          <button
                            role="menuitem"
                            className="w-full text-left px-3 py-2 text-xs font-semibold rounded-md text-primary hover:bg-surface-strong disabled:opacity-50"
                            onClick={() => {
                              handleMoveProduct(group.productId, 'up');
                              setOpenActionMenuId(null);
                            }}
                            disabled={index === 0 || isReordering}
                          >
                            {t('stock.reorderUp')}
                          </button>
                          <button
                            role="menuitem"
                            className="w-full text-left px-3 py-2 text-xs font-semibold rounded-md text-primary hover:bg-surface-strong disabled:opacity-50"
                            onClick={() => {
                              handleMoveProduct(group.productId, 'down');
                              setOpenActionMenuId(null);
                            }}
                            disabled={index === sortedGroupedStock.length - 1 || isReordering}
                          >
                            {t('stock.reorderDown')}
                          </button>
                          <button
                            role="menuitem"
                            className="w-full text-left px-3 py-2 text-xs font-semibold rounded-md text-danger hover:bg-danger-soft disabled:opacity-50"
                            onClick={() => {
                              handleDeactivateProduct(group.productId, group.productName);
                              setOpenActionMenuId(null);
                            }}
                            disabled={deletingProductId === group.productId}
                          >
                            {deletingProductId === group.productId ? t('stock.deleting') : t('stock.deactivateProduct')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {isEditing && (
                    <div className="space-y-2">
                      <label className="text-xs font-semibold text-muted block" htmlFor={`rename-${group.productId}`}>
                        {t('stock.renameLabel')}
                      </label>
                      <div className="flex flex-col gap-2 md:flex-row md:items-center">
                        <input
                          id={`rename-${group.productId}`}
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          className="flex-1 px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm focus:ring-2 focus:ring-[var(--color-focus)]"
                          placeholder={t('stock.renamePlaceholder')}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleRename(group.productId)}
                            disabled={savingProductId === group.productId || !editingName.trim()}
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent disabled:opacity-50"
                          >
                            {savingProductId === group.productId ? t('stock.saving') : t('stock.save')}
                          </button>
                          <button
                            onClick={cancelEditing}
                            disabled={savingProductId === group.productId}
                            className="px-3 py-2 rounded-lg text-sm font-semibold border border-subtle text-primary hover:bg-surface-strong disabled:opacity-50"
                          >
                            {t('stock.cancel')}
                          </button>
                        </div>
                      </div>
                      {renameError && <p className="text-danger text-xs">{renameError}</p>}
                    </div>
                  )}
                </div>

                {isExpanded && (
                  <div className="border-t border-subtle p-2 md:p-4 bg-surface-strong animate-in slide-in-from-top-2 fade-in">
                    {isEditingDetails && (
                      <div className="mb-4 rounded-lg border border-subtle bg-surface p-4 space-y-3">
                        <div className="text-sm font-semibold text-primary">{t('stock.editDetailsTitle')}</div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted" htmlFor={`product-name-${group.productId}`}>
                              {t('stock.fields.name')}
                            </label>
                            <input
                              id={`product-name-${group.productId}`}
                              value={productDetails.name}
                              onChange={(e) => setProductDetails(prev => ({ ...prev, name: e.target.value }))}
                              className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted" htmlFor={`product-base-${group.productId}`}>
                              {t('stock.fields.baseSku')}
                            </label>
                            <input
                              id={`product-base-${group.productId}`}
                              value={productDetails.baseSku}
                              onChange={(e) => setProductDetails(prev => ({ ...prev, baseSku: e.target.value }))}
                              className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                            />
                          </div>
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-semibold text-muted" htmlFor={`product-description-${group.productId}`}>
                            {t('stock.fields.description')}
                          </label>
                          <textarea
                            id={`product-description-${group.productId}`}
                            value={productDetails.description}
                            onChange={(e) => setProductDetails(prev => ({ ...prev, description: e.target.value }))}
                            rows={3}
                            className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                          />
                        </div>
                        <div className="grid gap-3 md:grid-cols-2">
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted" htmlFor={`product-color-${group.productId}`}>
                              {t('stock.fields.displayColor')}
                            </label>
                            <div className="flex items-center gap-3">
                              <input
                                id={`product-color-${group.productId}`}
                                type="color"
                                value={productDetails.displayColor}
                                onChange={(e) => setProductDetails(prev => ({ ...prev, displayColor: e.target.value }))}
                                className="h-10 w-16 border border-subtle rounded-lg bg-surface-strong"
                              />
                              <span className="text-xs text-muted">{productDetails.displayColor}</span>
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs font-semibold text-muted" htmlFor={`product-icon-${group.productId}`}>
                              {t('stock.fields.iconUrl')}
                            </label>
                            <input
                              id={`product-icon-${group.productId}`}
                              value={productDetails.iconUrl}
                              onChange={(e) => setProductDetails(prev => ({ ...prev, iconUrl: e.target.value }))}
                              placeholder={t('stock.fields.iconUrlPlaceholder')}
                              className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                            />
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-xs font-semibold text-muted">{t('stock.fields.iconPreview')}</div>
                          {productDetails.iconUrl ? (
                            <img
                              src={productDetails.iconUrl}
                              alt={t('stock.productIconPreviewAlt', { name: productDetails.name || group.productName })}
                              className="h-12 w-12 rounded-md border border-subtle object-cover"
                            />
                          ) : (
                            <div className="h-12 w-12 rounded-md border border-dashed border-subtle" />
                          )}
                        </div>
                        <label className="flex items-center gap-2 text-xs font-semibold text-muted">
                          <input
                            type="checkbox"
                            checked={productDetails.active}
                            onChange={(e) => setProductDetails(prev => ({ ...prev, active: e.target.checked }))}
                          />
                          {t('stock.fields.active')}
                        </label>
                        {productDetailsError && <p className="text-danger text-xs">{productDetailsError}</p>}
                        <div className="flex gap-2">
                          <button
                            className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent disabled:opacity-50"
                            onClick={() => handleSaveProductDetails(group.productId)}
                            disabled={savingProductDetailsId === group.productId}
                          >
                            {savingProductDetailsId === group.productId ? t('stock.saving') : t('stock.save')}
                          </button>
                          <button
                            className="px-3 py-2 rounded-lg text-sm font-semibold border border-subtle text-primary hover:bg-surface-strong"
                            onClick={cancelEditingProductDetails}
                            disabled={savingProductDetailsId === group.productId}
                          >
                            {t('stock.cancel')}
                          </button>
                        </div>
                      </div>
                    )}
                    {renderProductMatrix(group.variants, variantSortMode, category)}
                    <div className="mt-4 space-y-2">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="text-[11px] uppercase text-muted font-semibold tracking-wide">
                          {t('stock.variantActionsTitle')}
                        </div>
                        <label className="flex items-center gap-2 text-xs font-semibold text-muted">
                          <span>{t('stock.variantSortLabel')}</span>
                          <select
                            className="rounded-md border border-subtle bg-surface-strong px-2 py-1 text-xs text-primary"
                            value={variantSortMode}
                            onChange={(event) => setVariantSortModes(prev => ({
                              ...prev,
                              [group.productId]: event.target.value as VariantSortMode
                            }))}
                          >
                            <option value="manual">{t('stock.variantSortManual')}</option>
                            <option value="sku-asc">{t('stock.variantSortSkuAsc')}</option>
                            <option value="sku-desc">{t('stock.variantSortSkuDesc')}</option>
                            <option value="color-size-asc">{t('stock.variantSortColorSizeAsc')}</option>
                            <option value="color-size-desc">{t('stock.variantSortColorSizeDesc')}</option>
                          </select>
                        </label>
                      </div>
                      <div className="grid gap-2 md:grid-cols-2">
                        {sortedVariants.map((variant, variantIndex) => (
                          <div key={variant.id} className="rounded-lg border border-subtle bg-surface px-3 py-2 space-y-2">
                            <div className="flex items-center justify-between gap-3">
                              <div>
                                <div className="text-xs font-semibold text-primary">{variant.sku}</div>
                                <div className="text-[11px] text-muted">{variant.color} • {variant.size}</div>
                              </div>
                              <div className="flex items-center gap-2">
                                {isManualSort && (
                                  <div className="flex items-center gap-1">
                                    <button
                                      type="button"
                                      className="inline-flex items-center px-2 py-1 text-[11px] font-semibold rounded-md border border-subtle text-primary hover:bg-surface-strong disabled:opacity-50"
                                      onClick={() => handleMoveVariant(group.productId, variant.id, 'up')}
                                      disabled={variantIndex === 0 || reorderingVariantId === variant.id}
                                    >
                                      {t('stock.reorderUp')}
                                    </button>
                                    <button
                                      type="button"
                                      className="inline-flex items-center px-2 py-1 text-[11px] font-semibold rounded-md border border-subtle text-primary hover:bg-surface-strong disabled:opacity-50"
                                      onClick={() => handleMoveVariant(group.productId, variant.id, 'down')}
                                      disabled={variantIndex === sortedVariants.length - 1 || reorderingVariantId === variant.id}
                                    >
                                      {t('stock.reorderDown')}
                                    </button>
                                  </div>
                                )}
                                {editingVariantId !== variant.id && (
                                  <button
                                    className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md border border-subtle text-primary hover:bg-surface-strong"
                                    onClick={() => startEditingVariantDetails(variant)}
                                  >
                                    {t('stock.editDetails')}
                                  </button>
                                )}
                                <button
                                  className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md border border-danger text-danger hover:bg-danger-soft disabled:opacity-50"
                                  onClick={() => handleDeactivateVariant(variant)}
                                  disabled={deletingVariantId === variant.id}
                                >
                                  {deletingVariantId === variant.id ? t('stock.deleting') : t('stock.deactivateVariant')}
                                </button>
                              </div>
                            </div>
                            {editingVariantId === variant.id && (
                              <div className="space-y-2">
                                <div className="grid gap-2 md:grid-cols-3">
                                  <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted" htmlFor={`variant-sku-${variant.id}`}>
                                      {t('stock.fields.sku')}
                                    </label>
                                    <input
                                      id={`variant-sku-${variant.id}`}
                                      value={variantDetails.sku}
                                      onChange={(e) => setVariantDetails(prev => ({ ...prev, sku: e.target.value }))}
                                      className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted" htmlFor={`variant-color-${variant.id}`}>
                                      {t('stock.fields.color')}
                                    </label>
                                    <input
                                      id={`variant-color-${variant.id}`}
                                      value={variantDetails.color}
                                      onChange={(e) => setVariantDetails(prev => ({ ...prev, color: e.target.value }))}
                                      className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                                    />
                                  </div>
                                  <div className="space-y-1">
                                    <label className="text-xs font-semibold text-muted" htmlFor={`variant-size-${variant.id}`}>
                                      {t('stock.fields.size')}
                                    </label>
                                    <input
                                      id={`variant-size-${variant.id}`}
                                      value={variantDetails.size}
                                      onChange={(e) => setVariantDetails(prev => ({ ...prev, size: e.target.value }))}
                                      className="w-full px-3 py-2 border border-subtle rounded-lg bg-surface-strong text-sm"
                                    />
                                  </div>
                                </div>
                                <label className="flex items-center gap-2 text-xs font-semibold text-muted">
                                  <input
                                    type="checkbox"
                                    checked={variantDetails.active}
                                    onChange={(e) => setVariantDetails(prev => ({ ...prev, active: e.target.checked }))}
                                  />
                                  {t('stock.fields.active')}
                                </label>
                                {variantDetailsError && <p className="text-danger text-xs">{variantDetailsError}</p>}
                                <div className="flex gap-2">
                                  <button
                                    className="px-4 py-2 rounded-lg text-sm font-semibold bg-accent text-on-accent disabled:opacity-50"
                                    onClick={() => handleSaveVariantDetails(variant)}
                                    disabled={savingVariantId === variant.id}
                                  >
                                    {savingVariantId === variant.id ? t('stock.saving') : t('stock.save')}
                                  </button>
                                  <button
                                    className="px-3 py-2 rounded-lg text-sm font-semibold border border-subtle text-primary hover:bg-surface-strong"
                                    onClick={cancelEditingVariantDetails}
                                    disabled={savingVariantId === variant.id}
                                  >
                                    {t('stock.cancel')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* LIST VIEW */}
          {viewMode === 'LIST' && (
            <div className="bg-surface rounded-xl shadow-sm border border-subtle overflow-hidden">
               <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-surface-strong text-muted uppercase font-medium text-xs">
                    <tr>
                      <th className="px-4 py-3">{t('stock.table.product')}</th>
                      <th className="px-4 py-3">{t('stock.table.variant')}</th>
                      <th className="px-4 py-3 text-right">{t('stock.table.stock')}</th>
                      <th className="px-4 py-3 text-right">{t('stock.table.actions')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {stock.map((item) => (
                    <tr key={item.id} className="hover:bg-surface-strong transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="inline-flex h-2.5 w-2.5 rounded-full border border-subtle"
                            style={{ backgroundColor: item.productDisplayColor || '#111827' }}
                            aria-hidden="true"
                          />
                          {item.productIconUrl ? (
                            <img
                              src={item.productIconUrl}
                              alt={t('stock.productIconAlt', { name: formatProductName(item.productName) })}
                              className="h-5 w-5 rounded border border-subtle object-cover"
                            />
                          ) : (
                            <span className="h-5 w-5 rounded border border-dashed border-subtle" />
                          )}
                          <div className="font-medium text-primary">{formatProductName(item.productName)}</div>
                        </div>
                        <div className="text-xs text-muted font-mono">{item.sku}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded bg-surface-strong text-muted text-xs">
                          {item.color} <span className="text-muted">|</span> {item.size}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {(() => {
                          const category = item.base_sku || item.productName;
                          const status = getStatusForQuantity(item.current_stock, category);
                          const style = statusStyles[status];
                          const isEditingStock = editingStockId === item.id;

                          return (
                            <div className="flex items-center justify-end gap-2">
                              {isEditingStock ? (
                                <>
                                  <input
                                    type="number"
                                    className="w-24 px-2 py-1 border border-subtle rounded-lg bg-surface-strong text-right text-sm"
                                    value={editingStockValue}
                                    onChange={(e) => setEditingStockValue(e.target.value === '' ? '' : Number(e.target.value))}
                                    autoFocus
                                  />
                                  <div className="flex items-center gap-1">
                                    <button
                                      className="px-3 py-1 rounded-md text-xs font-semibold bg-accent text-on-accent disabled:opacity-50"
                                      onClick={() => handleConfirmStock(item)}
                                      disabled={savingStockId === item.id || editingStockValue === ''}
                                    >
                                      {savingStockId === item.id ? t('stock.saving') : t('stock.save')}
                                    </button>
                                    <button
                                      className="px-3 py-1 rounded-md text-xs font-semibold border border-subtle text-primary hover:bg-surface-strong"
                                      onClick={cancelEditingStock}
                                      disabled={savingStockId === item.id}
                                    >
                                      {t('stock.cancel')}
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <span
                                    className={`inline-flex items-center gap-2 px-3 py-1 rounded-md border text-sm font-bold ${style.bg} ${style.text} ${style.border}`}
                                    onClick={() => startEditingStock(item.id, item.current_stock)}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        startEditingStock(item.id, item.current_stock);
                                      }
                                    }}
                                  >
                                    {item.current_stock}
                                    <span className="text-[11px] text-muted font-semibold">{t('stock.alertSettings.thresholdValue', { value: getThresholdForCategory(category) })}</span>
                                  </span>
                                  <button
                                    className="text-xs font-semibold text-primary hover:underline"
                                    onClick={() => startEditingStock(item.id, item.current_stock)}
                                  >
                                    {t('stock.editStock')}
                                  </button>
                                </>
                              )}
                            </div>
                          );
                        })()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          className="inline-flex items-center px-3 py-1.5 text-xs font-semibold rounded-md border border-danger text-danger hover:bg-danger-soft disabled:opacity-50"
                          onClick={() => handleDeactivateVariant(item)}
                          disabled={deletingVariantId === item.id}
                        >
                          {deletingVariantId === item.id ? t('stock.deleting') : t('stock.deactivateVariant')}
                        </button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {toast && (
        <div className={`fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 rounded-full text-sm font-medium shadow-xl z-50 animate-in fade-in slide-in-from-top-2 ${
          toast.type === 'success' ? 'bg-accent text-white' : 'bg-danger text-white'
        }`}>
          {toast.msg}
        </div>
      )}
    </div>
  );
};
